import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  User,
  Edit,
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Shield,
  Clock,
  UserCheck,
  UserX,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Trash2,
  ContactIcon as Contact,
} from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { PAGE_COLORS } from '../../utils/pageColors';
import {
  useGetUserProfileByIdQuery,
  useChangeUserStatusMutation,
  useSoftDeleteUserProfileMutation,
  useGetUserActivitiesQuery,
} from '../../store/slices/apiSlice';
import type { UserProfile, UserActivity } from '../../utils/types';
import { useToast } from '../../hooks/useToast';
import { CompleteJobConfirmDialog } from '../../components/production-line/CompleteJobConfirmDialog';

interface UserDetailsPageProps {}

export const UserDetailsPage: React.FC<UserDetailsPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // API queries and mutations
  const {
    data: user,
    isLoading,
    error,
    refetch
  } = useGetUserProfileByIdQuery(id!, { skip: !id });

  const {
    data: activitiesResponse,
    isLoading: activitiesLoading
  } = useGetUserActivitiesQuery({
    user: user?.user_details.id,
    page_size: 10,
    ordering: '-created_at'
  }, { skip: !user?.user_details.id });

  const [changeUserStatus] = useChangeUserStatusMutation();
  const [softDeleteUser, { isLoading: isSoftDeleting }] = useSoftDeleteUserProfileMutation();

  const handleStatusChange = async (newStatus: 'active' | 'inactive') => {
    if (!user || !id) return;

    try {
      await changeUserStatus({
        profileId: id,
        status: newStatus,
      }).unwrap();

      toast.success('Status updated', `User status changed to ${newStatus}.`);
      refetch();
    } catch (err: any) {
      toast.error('Update failed', err?.data?.error || 'Failed to change user status');
    }
  };

  const handleSoftDelete = async () => {
    if (!id) return;
    try {
      await softDeleteUser({ profileId: id }).unwrap();
      setShowDeleteConfirm(false);
      toast.success('User deleted', 'Username and email can be reused for a new account.');
      navigate('/users');
    } catch (err: any) {
      toast.error('Delete failed', err?.data?.error || 'Failed to delete user');
    }
  };

  const getStatusBadge = (s: UserProfile['status']) => {
    const badges: Record<string, string> = {
      active: 'bg-green-100 text-green-800 border border-green-200',
      inactive: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      blocked: 'bg-red-100 text-red-800 border border-red-200',
      deleted: 'bg-gray-200 text-gray-800 border border-gray-300',
    };
    return badges[s] || badges.active;
  };

  const getStatusIcon = (s: UserProfile['status']) => {
    switch (s) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'inactive':
        return <XCircle className="w-4 h-4" />;
      case 'blocked':
        return <AlertTriangle className="w-4 h-4" />;
      case 'deleted':
        return <Trash2 className="w-4 h-4" />;
      default:
        return <CheckCircle className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <AppShell>
        <PageLayout
          title="Loading User"
          subtitle="Please wait..."
          bgColor={PAGE_COLORS.settings.bgColor}
          textColor={PAGE_COLORS.settings.textColor}
          borderColor={PAGE_COLORS.settings.borderColor}
          icon={<User className="w-6 h-6" />}
        >
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="large" />
          </div>
        </PageLayout>
      </AppShell>
    );
  }

  if (error || !user) {
    return (
      <AppShell>
        <PageLayout
          title="User Not Found"
          subtitle="The requested user could not be found"
          bgColor={PAGE_COLORS.settings.bgColor}
          textColor={PAGE_COLORS.settings.textColor}
          borderColor={PAGE_COLORS.settings.borderColor}
          icon={<User className="w-6 h-6" />}
        >
          <ContentCard title="User Not Found">
            <div className="text-center py-8">
              <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                User not found
              </h3>
              <p className="text-gray-600 mb-4">
                The user you're looking for doesn't exist or you don't have permission to view it.
              </p>
              <Button onClick={() => navigate('/users')}>
                Back to Users
              </Button>
            </div>
          </ContentCard>
        </PageLayout>
      </AppShell>
    );
  }

  const activities = activitiesResponse?.results || [];
  const isDeleted = user.status === 'deleted';

  return (
    <AppShell>
      <PageLayout
        title={user.full_name || 'User Profile'}
        subtitle={`@${user.user_details.username}`}
        bgColor={PAGE_COLORS.settings.bgColor}
        textColor={PAGE_COLORS.settings.textColor}
        borderColor={PAGE_COLORS.settings.borderColor}
        icon={<User className="w-6 h-6" />}
      >
        <CompleteJobConfirmDialog
          isOpen={showDeleteConfirm}
          title="Delete user?"
          leadContent={
            <>
              Remove access for{' '}
              <strong className="font-semibold text-gray-900">
                {user.full_name || user.user_details.username}
              </strong>{' '}
              (@{user.user_details.username})? Username and email can be reused for a new account.
            </>
          }
          sectionHeading="This will:"
          bullets={[
            'Disable login for this account',
            'Mark the profile as Deleted',
            'Free their username and email for a new user',
          ]}
          cancelLabel="Cancel"
          confirmLabel="Delete user"
          confirmingLabel="Deleting…"
          destructive
          isLoading={isSoftDeleting}
          onCancel={() => {
            if (!isSoftDeleting) setShowDeleteConfirm(false);
          }}
          onConfirm={() => {
            void handleSoftDelete();
          }}
          closeOnBackdropClick
        />

        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/users')}
            icon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Users
          </Button>

          <div className="flex items-center gap-2">
            {!isDeleted && (
              <>
                {user.status !== 'active' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStatusChange('active')}
                    icon={<UserCheck className="w-4 h-4" />}
                  >
                    Activate
                  </Button>
                )}
                {user.status === 'active' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleStatusChange('inactive')}
                    icon={<UserX className="w-4 h-4" />}
                  >
                    Deactivate
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  icon={<Trash2 className="w-4 h-4" />}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  Delete user
                </Button>
                <Link to={`/users/${user.id}/edit`}>
                  <Button size="sm" icon={<Edit className="w-4 h-4" />}>
                    Edit User
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Profile Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <ContentCard title="Profile Information">
              <div className="space-y-6">
                {/* User Avatar and Basic Info */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="h-20 w-20 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-white font-bold text-xl">
                        {user.user_details.first_name?.[0]}{user.user_details.last_name?.[0]}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-2xl font-bold text-gray-900">
                      {user.full_name || 'No name provided'}
                    </h3>
                    <p className="text-lg text-gray-600">@{user.user_details.username}</p>
                    <div className="mt-2">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(user.status)}`}>
                        {getStatusIcon(user.status)}
                        <span className="ml-1 capitalize">{user.status}</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contact Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Email */}
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Email</p>
                      <p className="text-gray-900">{user.user_details.email}</p>
                    </div>
                  </div>

                  {/* Phone */}
                  {user.phone && (
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <Phone className="w-5 h-5 text-green-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Phone</p>
                        <p className="text-gray-900">{user.phone}</p>
                      </div>
                    </div>
                  )}

                  {/* Date of Birth */}
                  {user.date_of_birth && (
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <Calendar className="w-5 h-5 text-purple-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Date of Birth</p>
                        <p className="text-gray-900">
                          {new Date(user.date_of_birth).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Account Created */}
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <Clock className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Member Since</p>
                      <p className="text-gray-900">
                        {new Date(user.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Address */}
                {user.address && (
                  <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                    <MapPin className="w-5 h-5 text-red-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-500">Address</p>
                      <p className="text-gray-900 whitespace-pre-line">{user.address}</p>
                    </div>
                  </div>
                )}
              </div>
            </ContentCard>

            {/* Emergency Contact */}
            {(user.emergency_contact_name || user.emergency_contact_phone) && (
              <ContentCard title="Emergency Contact">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {user.emergency_contact_name && (
                    <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
                      <Contact className="w-5 h-5 text-red-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Contact Name</p>
                        <p className="text-gray-900">{user.emergency_contact_name}</p>
                      </div>
                    </div>
                  )}

                  {user.emergency_contact_phone && (
                    <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
                      <Phone className="w-5 h-5 text-red-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Contact Phone</p>
                        <p className="text-gray-900">{user.emergency_contact_phone}</p>
                      </div>
                    </div>
                  )}
                </div>
              </ContentCard>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Role Information */}
            <ContentCard title="Role & Permissions">
              {user.role_details ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                    <Shield className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="font-medium text-gray-900">{user.role_details.name}</p>
                      {user.role_details.description && (
                        <p className="text-sm text-gray-600">{user.role_details.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <p>Users with this role: {user.role_details.user_count}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Shield className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">No role assigned</p>
                  <Link to={`/users/${user.id}/edit`} className="text-blue-600 hover:text-blue-800 text-sm">
                    Assign Role
                  </Link>
                </div>
              )}
            </ContentCard>

            {/* Account Status */}
            <ContentCard title="Account Status">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Status</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(user.status)}`}>
                    <span className="capitalize">{user.status}</span>
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Active Account</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    user.user_details.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {user.user_details.is_active ? 'Yes' : 'No'}
                  </span>
                </div>
                {user.user_details.last_login && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Last Login</span>
                    <span className="text-xs text-gray-900">
                      {new Date(user.user_details.last_login).toLocaleDateString()}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Password Changed</span>
                  <span className="text-xs text-gray-900">
                    {new Date(user.last_password_change).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </ContentCard>

            {/* Recent Activity */}
            <ContentCard title="Recent Activity">
              {activitiesLoading ? (
                <div className="flex justify-center py-4">
                  <LoadingSpinner size="small" />
                </div>
              ) : activities.length > 0 ? (
                <div className="space-y-3">
                  {activities.slice(0, 5).map((activity) => (
                    <div key={activity.id} className="flex items-start space-x-3 p-2 bg-gray-50 rounded">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900">{activity.description}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(activity.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {activities.length > 5 && (
                    <p className="text-xs text-gray-500 text-center pt-2">
                      And {activities.length - 5} more activities...
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600 text-sm">No recent activity</p>
                </div>
              )}
            </ContentCard>
          </div>
        </div>
      </PageLayout>
    </AppShell>
  );
};
